import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText, Gallery, ImportCurve, DocumentDownload } from "iconsax-react";
import { PdfViewer, ImageViewer, ModalWrapper, Button } from "@/shared/ui";
import type { WorkerDetailResponse } from "../types";
import type { WorkerDocument } from "../types";

interface Props {
  worker: WorkerDetailResponse;
}

type ViewerState =
  | { type: "pdf"; url: string; name: string; secondaryUrl?: string }
  | { type: "image"; url: string; name: string }
  | { type: "unsupported"; url: string; name: string }
  | null;

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]);
const NUMBERED_TYPES = new Set(["diploma", "diploma_transcript", "enrollment_verification", "social_category"]);

function getExtensionFromUrl(url: string): string {
  const path = url.split("?")[0];
  const lastDot = path.lastIndexOf(".");
  if (lastDot === -1) return "";
  return path.slice(lastDot + 1).toLowerCase();
}

function getFileType(url: string): "pdf" | "image" | "other" {
  const ext = getExtensionFromUrl(url);
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  return "other";
}

export default function DocumentsTab({ worker }: Props) {
  const { t } = useTranslation("WorkerDetailPage");
  const [viewer, setViewer] = useState<ViewerState>(null);

  const documents = worker.documents ?? [];

  const numberingMap = useMemo(function buildNumberingMap() {
    const counts = new Map<string, number>();
    const indices = new Map<string, number>();
    for (const doc of documents) {
      if (NUMBERED_TYPES.has(doc.document_type)) {
        counts.set(doc.document_type, (counts.get(doc.document_type) ?? 0) + 1);
      }
    }
    const result = new Map<string, number>();
    for (const doc of documents) {
      if (NUMBERED_TYPES.has(doc.document_type) && (counts.get(doc.document_type) ?? 0) > 1) {
        const idx = (indices.get(doc.document_type) ?? 0) + 1;
        indices.set(doc.document_type, idx);
        result.set(doc.id, idx);
      }
    }
    return result;
  }, [documents]);

  if (documents.length === 0) {
    return (
      <div className="p-7 radius-3xl surface-base-fill flex items-center justify-center min-h-[200px]">
        <p className="text-body-regular-md content-base-secondary">{t("documents.noDocuments")}</p>
      </div>
    );
  }

  function getDocumentLabel(doc: WorkerDocument): string {
    const key = `documents.types.${doc.document_type}`;
    const translated = t(key);
    const base = translated === key ? doc.name : translated;
    const number = numberingMap.get(doc.id);
    if (number != null) return `${base} №${number}`;
    return base;
  }

  function handleDocumentClick(doc: WorkerDocument) {
    const fileType = getFileType(doc.file_url);
    const label = getDocumentLabel(doc);
    const secondaryUrl = doc.signed_pdf_url && doc.original_file_url ? doc.original_file_url : undefined;
    if (fileType === "pdf") {
      setViewer({ type: "pdf", url: doc.file_url, name: label, secondaryUrl });
    } else if (fileType === "image") {
      setViewer({ type: "image", url: doc.file_url, name: label });
    } else {
      setViewer({ type: "unsupported", url: doc.file_url, name: label });
    }
  }

  function handleDownload(e: React.MouseEvent, doc: WorkerDocument) {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = doc.file_url;
    link.download = doc.name;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function getDocumentIcon(url: string) {
    const fileType = getFileType(url);
    if (fileType === "image") return Gallery;
    return DocumentText;
  }

  return (
    <>
      <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-5">
        <h2 className="text-body-bold-lg content-base-primary pb-3 border-b surface-app-stroke">
          {t("documents.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => {
            const Icon = getDocumentIcon(doc.file_url);
            return (
              <div
                key={doc.id}
                role="button"
                tabIndex={0}
                onClick={() => handleDocumentClick(doc)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleDocumentClick(doc);
                  }
                }}
                className="flex items-center p-1 radius-md surface-component-fill cursor-pointer hover:elevation-level-2 transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/15">
                <div className="flex justify-between items-center flex-1 radius-sm p-4 surface-base-fill">
                  <div className="flex justify-start items-center gap-3 min-w-0">
                    <span className="content-base-low shrink-0">
                      <Icon size={16} color="currentColor" />
                    </span>
                    <p className="truncate text-label-md content-base-primary">{getDocumentLabel(doc)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDownload(e, doc)}
                    className="content-action-neutral cursor-pointer shrink-0 ml-2">
                    <ImportCurve size={18} color="currentColor" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {viewer?.type === "pdf" && (
        <PdfViewer
          fileUrl={viewer.url}
          title={viewer.name}
          secondaryFileUrl={viewer.secondaryUrl}
          secondaryFileName={viewer.name}
          onClose={() => setViewer(null)}
        />
      )}

      {viewer?.type === "image" && (
        <ImageViewer imageUrl={viewer.url} fileName={viewer.name} onClose={() => setViewer(null)} />
      )}

      {viewer?.type === "unsupported" && (
        <ModalWrapper onClose={() => setViewer(null)} width="w-md">
          <p className="text-body-bold-lg content-base-primary truncate pr-8 shrink-0">{viewer.name}</p>
          <div className="flex flex-col items-center justify-center gap-4 py-10">
            <p className="text-body-regular-md content-base-secondary">{t("documents.unsupportedFormat")}</p>
            <Button
              size="md"
              onClick={() => {
                const link = document.createElement("a");
                link.href = viewer.url;
                link.download = viewer.name;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}>
              <DocumentDownload size={16} color="currentColor" />
              {t("documents.download")}
            </Button>
          </div>
        </ModalWrapper>
      )}
    </>
  );
}

